import { RRule, Options as RRuleOptions } from 'rrule'

/**
 * node-ical 0.25.6의 RRule wrapper options(rrule-temporal 형식)를
 * rrule.js Options 형식으로 변환한다.
 *
 * rrule-temporal 키: byDay, byMonthDay, byYearDay, byWeekNo, byHour, byMinute, bySecond, bySetPos, byMonth
 * rrule.js 키:       byweekday, bymonthday, byyearday, byweekno, byhour, byminute, bysecond, bysetpos, bymonth
 */
const FREQ_MAP: Record<string, number> = {
  YEARLY: RRule.YEARLY,
  MONTHLY: RRule.MONTHLY,
  WEEKLY: RRule.WEEKLY,
  DAILY: RRule.DAILY,
  HOURLY: RRule.HOURLY,
  MINUTELY: RRule.MINUTELY,
  SECONDLY: RRule.SECONDLY,
}

const KEY_MAP: Record<string, string> = {
  byDay: 'byweekday',
  byMonthDay: 'bymonthday',
  byYearDay: 'byyearday',
  byWeekNo: 'byweekno',
  bySetPos: 'bysetpos',
  byMonth: 'bymonth',
}

/**
 * rrule-temporal의 byDay 문자열 코드(e.g. "TU", "FR", "1MO", "-1SU")를
 * rrule.js의 Weekday 객체로 변환한다.
 */
const WEEKDAY_MAP: Record<string, typeof RRule.MO> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
}

function convertByDay(days: unknown[]): unknown[] {
  return days
    .map((day) => {
      // rrule.js numeric weekday (0=MO ... 6=SU) — pass through
      if (typeof day === 'number') return day
      // 이미 rrule.js Weekday 객체(또는 mock {weekday: number})로 정규화된 경우 그대로 통과
      if (
        typeof day === 'object' &&
        day !== null &&
        typeof (day as { weekday?: unknown }).weekday === 'number'
      ) {
        return day
      }
      if (typeof day !== 'string') return null
      // "1MO", "-1SU" 등 ordinal prefix 처리
      const match = day.match(/^([+-]?\d+)?([A-Z]{2})$/)
      if (!match) return null
      const weekday = WEEKDAY_MAP[match[2]]
      if (!weekday) return null
      const nth = match[1] ? parseInt(match[1], 10) : undefined
      return nth !== undefined ? weekday.nth(nth) : weekday
    })
    .filter((w) => w !== null)
}

const SKIP_KEYS = new Set([
  'exDate',
  'rDate',
  'strict',
  'maxIterations',
  'includeDtstart',
  'rscale',
  'skip',
  // rrule-temporal(camelCase) 및 rrule.js(lowercase) 모두에서
  // 내부적으로 추가되는 시간 필드. 원본 RRULE에는 없었던 기본값이며,
  // rrule.js에 전달하면 DTSTART 시간을 덮어써서 오동작한다
  // (예: BYHOUR=0 → 00:00 강제, DTSTART 09:00 무시).
  'byHour',
  'byMinute',
  'bySecond',
  'byhour',
  'byminute',
  'bysecond',
])

function temporalToDate(value: unknown): Date | null {
  if (value instanceof Date) return value
  if (
    typeof value === 'object' &&
    value !== null &&
    'epochMilliseconds' in (value as Record<string, unknown>)
  ) {
    return new Date(Number((value as Record<string, unknown>).epochMilliseconds) / 1_000_000)
  }
  return null
}

export function convertToRRuleOptions(opts: Record<string, unknown>): Partial<RRuleOptions> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(opts)) {
    if (SKIP_KEYS.has(key) || value == null) continue

    if (key === 'freq' && typeof value === 'string') {
      result.freq = FREQ_MAP[value] ?? value
    } else if (key === 'dtstart') {
      result.dtstart = temporalToDate(value) ?? value
    } else if (key === 'until') {
      result.until = temporalToDate(value) ?? value
    } else if (key === 'byDay' || key === 'byweekday') {
      // rrule-temporal stores byDay as string codes (e.g. ["TU", "FR"]).
      // rrule.js requires Weekday objects (RRule.TU, RRule.FR).
      if (Array.isArray(value)) {
        result.byweekday = convertByDay(value)
      } else {
        // 단일 Weekday 객체/숫자/문자열 입력도 그대로 보존
        result.byweekday = value
      }
    } else if (KEY_MAP[key]) {
      result[KEY_MAP[key]] = value
    } else {
      result[key] = value
    }
  }
  return result as Partial<RRuleOptions>
}
